import { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Checkbox,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import TableChartIcon from '@mui/icons-material/TableChart';
import DescriptionIcon from '@mui/icons-material/Description';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PreviewIcon from '@mui/icons-material/Preview';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  DATASET_CONFIG,
  EXPORT_PRESETS,
  getColumnsForDataset,
  getDefaultExportMode,
  getDefaultOrderedKeys,
  getColumnLabel,
  groupColumns,
  insertKeyInLogicalOrder,
  sortKeysByColumnOrder,
} from '../utils/exportColumnDefinitions';
import {
  prepareExportPayload,
  buildExportMatrix,
  buildInventarioRecords,
  buildVentasHeaderRecords,
  buildVentasDetailRecords,
  buildComprasHeaderRecords,
  buildComprasDetailRecords,
  mergeVentasFromStore,
  mergeComprasFromStore,
  filterByDateRange,
  getDefaultExportDateFrom,
  getDefaultExportDateTo,
} from '../utils/exportRetailData';

const PREVIEW_ROW_LIMIT = 8;

function ColumnOrderStrip({ orderedKeys, columns, onReorder, onRemove }) {
  if (orderedKeys.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
        Selecciona columnas para ver el orden de exportación
      </Typography>
    );
  }

  return (
    <DragDropContext
      onDragEnd={(result) => {
        if (!result.destination) return;
        const next = Array.from(orderedKeys);
        const [removed] = next.splice(result.source.index, 1);
        next.splice(result.destination.index, 0, removed);
        onReorder(next);
      }}
    >
      <Droppable droppableId="export-columns" direction="horizontal">
        {(provided) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              p: 1.5,
              minHeight: 56,
              bgcolor: 'grey.50',
              borderRadius: 1,
              border: '1px dashed',
              borderColor: 'divider',
            }}
          >
            {orderedKeys.map((key, index) => (
              <Draggable key={key} draggableId={key} index={index}>
                {(dragProvided, snapshot) => (
                  <Chip
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    icon={
                      <Box {...dragProvided.dragHandleProps} sx={{ display: 'flex', alignItems: 'center', pl: 0.5 }}>
                        <DragIndicatorIcon sx={{ fontSize: 16 }} />
                      </Box>
                    }
                    label={
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                          {index + 1}.
                        </Typography>
                        <span>{getColumnLabel(columns, key)}</span>
                      </Stack>
                    }
                    onDelete={() => onRemove(key)}
                    size="small"
                    sx={{
                      height: 32,
                      bgcolor: snapshot.isDragging ? 'primary.light' : 'background.paper',
                      boxShadow: snapshot.isDragging ? 3 : 0,
                    }}
                  />
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    </DragDropContext>
  );
}

function ExportPreviewTable({ headers, rows }) {
  if (!headers.length) return null;

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 280 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {headers.map((h) => (
              <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'grey.100' }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={headers.length} align="center" sx={{ py: 3 }}>
                <Typography variant="body2" color="text.secondary">Sin datos para previsualizar</Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, ri) => (
              <TableRow key={ri} hover>
                {row.map((cell, ci) => (
                  <TableCell key={ci} sx={{ whiteSpace: 'nowrap', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {cell ?? ''}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function ExportDataDialog({
  open,
  onClose,
  dataset,
  store,
  inventarioProductos,
  ventas,
  compras,
}) {
  const config = DATASET_CONFIG[dataset] || {};
  const defaultMode = getDefaultExportMode(dataset);

  const [format, setFormat] = useState('xlsx');
  const [mode, setMode] = useState(defaultMode);
  const [preset, setPreset] = useState(EXPORT_PRESETS.essential);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState(getDefaultExportDateFrom);
  const [dateTo, setDateTo] = useState(getDefaultExportDateTo);

  const hasDateRange = dataset === 'ventas' || dataset === 'compras';
  const dateRangeInvalid = hasDateRange && dateFrom && dateTo && dateFrom > dateTo;

  const columns = useMemo(
    () => getColumnsForDataset(dataset, mode),
    [dataset, mode]
  );

  const [orderedSelectedKeys, setOrderedSelectedKeys] = useState(() =>
    getDefaultOrderedKeys(columns, EXPORT_PRESETS.essential)
  );

  const resetToRecommended = useCallback(() => {
    setPreset(EXPORT_PRESETS.essential);
    setOrderedSelectedKeys(getDefaultOrderedKeys(columns, EXPORT_PRESETS.essential));
  }, [columns]);

  useEffect(() => {
    if (open) {
      const initialMode = getDefaultExportMode(dataset);
      const initialCols = getColumnsForDataset(dataset, initialMode);
      setPreset(EXPORT_PRESETS.essential);
      setSearch('');
      setFormat('xlsx');
      setMode(initialMode);
      setDateFrom(getDefaultExportDateFrom());
      setDateTo(getDefaultExportDateTo());
      setOrderedSelectedKeys(getDefaultOrderedKeys(initialCols, EXPORT_PRESETS.essential));
    }
  }, [open, dataset]);

  useEffect(() => {
    if (preset === EXPORT_PRESETS.custom) return;
    if (preset === EXPORT_PRESETS.essential) {
      setOrderedSelectedKeys(getDefaultOrderedKeys(columns, EXPORT_PRESETS.essential));
    } else if (preset === EXPORT_PRESETS.all) {
      setOrderedSelectedKeys(getDefaultOrderedKeys(columns, EXPORT_PRESETS.all));
    } else if (preset === EXPORT_PRESETS.none) {
      setOrderedSelectedKeys([]);
    }
  }, [preset, columns]);

  const records = useMemo(() => {
    if (dataset === 'inventario') {
      return buildInventarioRecords(inventarioProductos || store?.productos || []);
    }
    if (dataset === 'ventas') {
      const merged = mergeVentasFromStore(store);
      const filtered = filterByDateRange(merged, dateFrom, dateTo);
      const ventaIds = new Set(filtered.map((x) => x.id));
      const det = (store?.detalleVentas || []).filter((d) => ventaIds.has(d.venta_id));
      return mode === 'detail'
        ? buildVentasDetailRecords(filtered, det)
        : buildVentasHeaderRecords(filtered, det);
    }
    if (dataset === 'compras') {
      const merged = mergeComprasFromStore(store);
      const filtered = filterByDateRange(merged, dateFrom, dateTo);
      const compraIds = new Set(filtered.map((x) => x.id));
      const det = (store?.detalleCompras || []).filter((d) => compraIds.has(d.compra_id));
      return mode === 'detail'
        ? buildComprasDetailRecords(filtered, det)
        : buildComprasHeaderRecords(filtered, det);
    }
    return [];
  }, [dataset, mode, inventarioProductos, store, dateFrom, dateTo]);

  const previewMatrix = useMemo(() => {
    if (orderedSelectedKeys.length === 0) return { headers: [], rows: [] };
    const { headers, rows } = buildExportMatrix(columns, orderedSelectedKeys, records, { store });
    return { headers, rows: rows.slice(0, PREVIEW_ROW_LIMIT) };
  }, [columns, orderedSelectedKeys, records, store]);

  const filteredGroups = useMemo(() => {
    const groups = groupColumns(columns);
    if (!search.trim()) return groups;
    const q = search.toLowerCase();
    const filtered = {};
    Object.entries(groups).forEach(([group, groupCols]) => {
      const match = groupCols.filter(
        (c) => c.label.toLowerCase().includes(q) || c.key.toLowerCase().includes(q)
      );
      if (match.length) filtered[group] = match;
    });
    return filtered;
  }, [columns, search]);

  const toggleKey = (key) => {
    setPreset(EXPORT_PRESETS.custom);
    setOrderedSelectedKeys((prev) =>
      prev.includes(key)
        ? prev.filter((k) => k !== key)
        : insertKeyInLogicalOrder(prev, key, columns)
    );
  };

  const toggleGroup = (groupCols, selectAll) => {
    setPreset(EXPORT_PRESETS.custom);
    const keys = groupCols.map((c) => c.key);
    setOrderedSelectedKeys((prev) => {
      if (selectAll) {
        const merged = [...prev];
        keys.forEach((k) => {
          if (!merged.includes(k)) merged.push(k);
        });
        return sortKeysByColumnOrder(merged, columns);
      }
      return prev.filter((k) => !keys.includes(k));
    });
  };

  const handleModeChange = (newMode) => {
    if (!newMode) return;
    setMode(newMode);
    setPreset(EXPORT_PRESETS.essential);
    const newCols = getColumnsForDataset(dataset, newMode);
    setOrderedSelectedKeys(getDefaultOrderedKeys(newCols, EXPORT_PRESETS.essential));
  };

  const handleExport = () => {
    if (orderedSelectedKeys.length === 0 || dateRangeInvalid) return;
    prepareExportPayload({
      dataset,
      mode,
      orderedSelectedKeys,
      columns,
      records,
      ctx: { store },
      filenameBase: config.filename || dataset,
      format,
      dateFrom: hasDateRange ? dateFrom : undefined,
      dateTo: hasDateRange ? dateTo : undefined,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle sx={{ pb: 1 }}>
        Exportar {config.title}
        <Typography variant="body2" color="text.secondary" fontWeight={400}>
          {records.length} fila(s) · {orderedSelectedKeys.length} columna(s) · vista previa de {Math.min(PREVIEW_ROW_LIMIT, records.length)} filas
          {hasDateRange && dateFrom && dateTo && !dateRangeInvalid && (
            <> · {dateFrom} al {dateTo}</>
          )}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={5}>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Formato</Typography>
                <ToggleButtonGroup value={format} exclusive size="small" onChange={(_, v) => v && setFormat(v)}>
                  <ToggleButton value="xlsx">
                    <TableChartIcon sx={{ mr: 0.75, fontSize: 18 }} /> Excel
                  </ToggleButton>
                  <ToggleButton value="csv">
                    <DescriptionIcon sx={{ mr: 0.75, fontSize: 18 }} /> CSV
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {config.modes && (
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>Vista de datos</Typography>
                  <ToggleButtonGroup
                    value={mode}
                    exclusive
                    size="small"
                    onChange={(_, v) => handleModeChange(v)}
                    sx={{ flexWrap: 'wrap' }}
                  >
                    {config.modes.map((m) => (
                      <ToggleButton key={m.key} value={m.key} sx={{ textTransform: 'none' }}>
                        {m.label}
                        <Typography component="span" variant="caption" sx={{ ml: 0.75, opacity: 0.7 }}>
                          ({m.description})
                        </Typography>
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
              )}

              {hasDateRange && (
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>Rango de fechas</Typography>
                  <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                    <TextField
                      label="Desde"
                      type="date"
                      size="small"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 150 }}
                    />
                    <TextField
                      label="Hasta"
                      type="date"
                      size="small"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 150 }}
                    />
                  </Stack>
                  {dateRangeInvalid && (
                    <Alert severity="error" sx={{ mt: 1, py: 0 }}>
                      La fecha inicial no puede ser posterior a la final.
                    </Alert>
                  )}
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                    Incluye el día final del rango. Por defecto: mes en curso hasta hoy.
                  </Typography>
                </Box>
              )}

              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700}>Columnas</Typography>
                  <Button size="small" startIcon={<RestartAltIcon />} onClick={resetToRecommended} sx={{ textTransform: 'none' }}>
                    Orden recomendado
                  </Button>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                  <Chip
                    label="Esenciales"
                    color={preset === EXPORT_PRESETS.essential ? 'primary' : 'default'}
                    onClick={() => setPreset(EXPORT_PRESETS.essential)}
                    variant={preset === EXPORT_PRESETS.essential ? 'filled' : 'outlined'}
                    size="small"
                  />
                  <Chip label="Todas" onClick={() => setPreset(EXPORT_PRESETS.all)} size="small" variant={preset === EXPORT_PRESETS.all ? 'filled' : 'outlined'} color={preset === EXPORT_PRESETS.all ? 'primary' : 'default'} />
                  <Chip label="Ninguna" onClick={() => setPreset(EXPORT_PRESETS.none)} size="small" variant={preset === EXPORT_PRESETS.none ? 'filled' : 'outlined'} />
                </Stack>
                <TextField
                  size="small"
                  placeholder="Buscar columna..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                    ),
                  }}
                />
              </Box>

              {orderedSelectedKeys.length === 0 && (
                <Alert severity="warning">Selecciona al menos una columna.</Alert>
              )}

              <Box sx={{ maxHeight: 260, overflowY: 'auto', pr: 0.5 }}>
                {Object.entries(filteredGroups).map(([group, groupCols]) => {
                  const allSelected = groupCols.every((c) => orderedSelectedKeys.includes(c.key));
                  const someSelected = groupCols.some((c) => orderedSelectedKeys.includes(c.key));
                  return (
                    <Box key={group} sx={{ mb: 1.5 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary">{group.toUpperCase()}</Typography>
                        <Button size="small" onClick={() => toggleGroup(groupCols, !allSelected)} sx={{ textTransform: 'none', minWidth: 0, fontSize: 11 }}>
                          {allSelected ? 'Quitar' : someSelected ? 'Completar' : 'Seleccionar'}
                        </Button>
                      </Stack>
                      {groupCols.map((colDef) => (
                        <FormControlLabel
                          key={colDef.key}
                          control={
                            <Checkbox
                              size="small"
                              checked={orderedSelectedKeys.includes(colDef.key)}
                              onChange={() => toggleKey(colDef.key)}
                            />
                          }
                          label={
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <span>{colDef.label}</span>
                              {colDef.essential && <Chip label="★" size="small" sx={{ height: 16, fontSize: 9 }} />}
                            </Stack>
                          }
                          sx={{ m: 0, display: 'flex', width: '100%' }}
                        />
                      ))}
                      <Divider sx={{ mt: 1 }} />
                    </Box>
                  );
                })}
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={12} lg={7}>
            <Stack spacing={2}>
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <PreviewIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2" fontWeight={700}>Orden de columnas</Typography>
                  <Typography variant="caption" color="text.secondary">Arrastra para reordenar</Typography>
                </Stack>
                <ColumnOrderStrip
                  orderedKeys={orderedSelectedKeys}
                  columns={columns}
                  onReorder={(keys) => {
                    setPreset(EXPORT_PRESETS.custom);
                    setOrderedSelectedKeys(keys);
                  }}
                  onRemove={(key) => {
                    setPreset(EXPORT_PRESETS.custom);
                    setOrderedSelectedKeys((prev) => prev.filter((k) => k !== key));
                  }}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Vista previa del archivo
                </Typography>
                <ExportPreviewTable headers={previewMatrix.headers} rows={previewMatrix.rows} />
                {records.length > PREVIEW_ROW_LIMIT && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                    Mostrando {PREVIEW_ROW_LIMIT} de {records.length} filas. La descarga incluirá todos los registros.
                  </Typography>
                )}
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          disabled={orderedSelectedKeys.length === 0 || records.length === 0 || dateRangeInvalid}
        >
          Descargar {format === 'xlsx' ? 'Excel' : 'CSV'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function ExportDataButton({
  dataset,
  store,
  inventarioProductos,
  ventas,
  compras,
  size = 'small',
  variant = 'outlined',
  label = 'Exportar',
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button size={size} variant={variant} startIcon={<DownloadIcon />} onClick={() => setOpen(true)}>
        {label}
      </Button>
      <ExportDataDialog
        open={open}
        onClose={() => setOpen(false)}
        dataset={dataset}
        store={store}
        inventarioProductos={inventarioProductos}
        ventas={ventas}
        compras={compras}
      />
    </>
  );
}
